import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Alert } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IPaymentInfo } from "src/app-telehealth-demo/util/utils";

interface PaymentInfoStepProps {
    paymentInfo: Partial<IPaymentInfo>;
    updatePaymentInfo: (data: Partial<IPaymentInfo>) => void;
    onContinue: () => void;
    onBack: () => void;
}

// Form validation schema
const schema = yup.object().shape({
    creditCardNumber: yup
        .string()
        .required("Credit card number is required")
        .matches(/^\d{16}$/, "Credit card number must be 16 digits"),
    expirationDate: yup
        .string()
        .required("Expiration date is required")
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "Expiration date must be in MM/YY format"),
    cvv: yup
        .string()
        .required("CVV is required")
        .matches(/^\d{3,4}$/, "CVV must be 3 or 4 digits"),
});

const PaymentInfoStep: React.FC<PaymentInfoStepProps> = ({
    paymentInfo,
    updatePaymentInfo,
    onContinue,
    onBack,
}) => {
    // Initialize react-hook-form
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: {
            creditCardNumber: paymentInfo.creditCardNumber || "",
            expirationDate: paymentInfo.expirationDate || "",
            cvv: paymentInfo.cvv || "",
        },
    });

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (type === "change") {
                updatePaymentInfo(value);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, updatePaymentInfo]);

    const onSubmit = (data: IPaymentInfo) => {
        updatePaymentInfo(data);
        onContinue();
    };

    // Format credit card number as user types
    const formatCreditCard = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 16) {
            value = value.slice(0, 16);
        }
        e.target.value = value;
    };

    // Format expiration date as user types (MM/YY)
    const formatExpirationDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 4) {
            value = value.slice(0, 4);
        }
        if (value.length > 2) {
            value = value.slice(0, 2) + "/" + value.slice(2);
        }
        e.target.value = value;
    };

    return (
        <div className="payment-info-step">
            <h2 className="mb-4">Payment Information</h2>
            <p className="text-muted mb-4">
                Please provide your payment details to complete your registration.
            </p>

            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Card className="mb-4">
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Credit Card Number</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="1234567890123456"
                                isInvalid={!!errors.creditCardNumber}
                                {...register("creditCardNumber")}
                                onChange={formatCreditCard}
                                maxLength={16}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.creditCardNumber?.message}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Expiration Date</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="MM/YY"
                                        isInvalid={!!errors.expirationDate}
                                        {...register("expirationDate")}
                                        onChange={formatExpirationDate}
                                        maxLength={5}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.expirationDate?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>CVV</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="123"
                                        isInvalid={!!errors.cvv}
                                        {...register("cvv")}
                                        maxLength={4}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.cvv?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        Back
                    </Button>
                    <Button variant="primary" type="submit">
                        Continue
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default PaymentInfoStep;
